import { useEffect, useState } from 'react';
import { ArrowRightIcon, ClockIcon, InfoIcon, PhoneIcon } from 'lucide-react';
import { business, deliveryDockAlt, images, routes } from '../data/site';
import { SiteLink } from '../components/SiteLink';
const TITLE = 'Hot Tubs in Brooklyn, MI | Sundance & Viking Spas';
const DESCRIPTION = 'Compare Sundance and Viking hot tubs, available and certified pre-owned spas, financing, and local showroom guidance from Pegasus Spas in Brooklyn, MI.';
const CANONICAL = 'https://www.pegasus-spas.com/hot-tubs';
const paths = [{
  title: 'Sundance Spas',
  copy: 'Comfort-focused seating, varied jet experiences and connected ownership features to compare in person.',
  linkLabel: 'Explore Sundance Spas',
  href: routes.sundance,
  image: images.sundance,
  modelLabel: 'Model: Cameo',
  alt: 'Sundance Spas Cameo hot tub in a landscaped backyard'
}, {
  title: 'Viking Spas',
  copy: 'American-built choices ranging from straightforward first-spa options to more fully featured models.',
  linkLabel: 'Explore Viking Spas',
  href: routes.viking,
  image: images.viking,
  modelLabel: 'Model: Regal',
  alt: 'Viking Spas Regal hot tub installed beside a modest Michigan home and deck'
}, {
  title: 'Available Spas',
  copy: "See the opportunities we're highlighting now and ask us for details.",
  linkLabel: 'See Available Spas',
  href: routes.availableSpas,
  image: images.pathHotTubs,
  modelLabel: null,
  alt: 'Hot tub with clear water and jets running on a residential wood deck surrounded by trees'
}, {
  title: 'Certified Pre-Owned Spas',
  copy: 'Explore previously owned options when available and ask us for current details.',
  linkLabel: 'Explore Pre-Owned Spas',
  href: routes.preOwned,
  image: images.hero,
  modelLabel: null,
  alt: 'Hot tub on a patio beside a Michigan lake home with lawn and trees'
}];
const useChoices = [{
  id: 'quiet',
  label: 'Quiet time for one or two',
  lead: 'A smaller spa can be the easiest to fit, heat and enjoy on an ordinary weeknight.',
  questions: ['How many people will normally be in the water at once?', 'Do you want a lounge seat to stretch out, or open seating you can move around?']
}, {
  id: 'family',
  label: 'Family time and conversation',
  lead: 'Seating layout and entry matter more than a feature list when several people share the spa.',
  questions: ['Will children, teenagers or guests use it along with the household?', 'Is there room to walk around the spa and get in and out comfortably?']
}, {
  id: 'massage',
  label: 'A favorite seat with stronger massage',
  lead: 'Different seats and jet arrangements feel different, so the seat you sit in matters most.',
  questions: ['Where do you want the jets to reach — shoulders, back or legs?', 'How deep and adjustable does that seat need to feel to you?']
}, {
  id: 'first',
  label: 'A straightforward first spa',
  lead: 'A first spa is usually about placement, routine care and simple controls.',
  questions: ['Where will it sit, and how will it reach that spot?', 'What water-care options and filter-cleaning routine would suit you?']
}];
const packageItems = ['Spa cover', 'Standard cover lift', 'Standard steps', 'Basic startup kit', 'Standard local delivery', 'Setup and orientation', 'A Ducky'];
const journey = [{
  number: '01',
  title: 'Plan the spot',
  copy: 'The spa needs a firm, flat, level spot and a working spigot and hose nearby. Most hot tubs run on 240V power with a 50- or 60-amp service, though the exact requirement depends on the model, and we give you the exact specifications for the one you choose. Not sure where it should go? We are happy to talk it through, and we can come out and take another look at the space at no charge and with no pressure.'
}, {
  number: '02',
  title: 'Delivery by our own team',
  copy: 'We deliver with our own team rather than a contract delivery crew. We review access and placement with you in advance; complex sites may require separate arrangements.'
}, {
  number: '03',
  title: 'Setup and orientation',
  copy: 'We walk you through the controls and programming, the filter, basic water care and cabinet basics.'
}, {
  number: '04',
  title: 'Enjoy it',
  copy: 'You can often enjoy the spa that same evening, and we are still here when questions come up.'
}];
const relatedLinks = [{
  label: 'Replacement Covers',
  href: routes.covers
}, {
  label: 'Cover Lifts',
  href: routes.coverLifts
}, {
  label: 'Spa Care & FAQs',
  href: routes.spaCare
}, {
  label: 'Shop Filters & Supplies',
  href: routes.supplies
}];
function upsertMeta(name: string, content: string) {
  let el = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}
export function HotTubsPage() {
  const [openChoice, setOpenChoice] = useState<string>(useChoices[0].id);
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = TITLE;
    upsertMeta('description', DESCRIPTION);
    let canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', CANONICAL);
  }, []);
  return <main data-id="element-1458" data-mp="3knhn3pc4us40">
      {/* Intro band */}
      <section aria-labelledby="hot-tubs-h1" className="relative w-full" data-id="element-1459" data-mp="3kmezpre0r7su">
        <div className="relative w-full overflow-hidden min-h-[420px] sm:min-h-[400px] lg:h-[440px]" data-id="element-1460" data-mp="3pmudgk27qhbc">
          <img src={images.pathHotTubs} alt="Hot tub with clear water and jets running on a residential wood deck surrounded by trees" className="absolute inset-0 h-full w-full object-cover [object-position:50%_58%]" width={1600} height={1200} decoding="async" data-id="element-1461" data-mp="3too9gf1gqx56" />
          <div className="hero-scrim-compact absolute inset-0 sm:hidden" aria-hidden="true" data-id="element-1462" data-mp="23davpilegfxy" />
          <div className="hero-scrim absolute inset-0 hidden sm:block" aria-hidden="true" data-id="element-1463" data-mp="1yidhzyd6dieq" />

          <div className="relative mx-auto flex min-h-[420px] max-w-container flex-col justify-center px-5 py-10 sm:min-h-[400px] sm:px-8 lg:h-[440px] lg:min-h-0" data-id="element-1464" data-mp="2d7jgfk46v4lk">
            <p className="text-[14px] font-medium uppercase text-white sm:text-[15px]" data-id="element-1465" data-mp="2m914bonu8akt">
              Hot Tubs at Pegasus
            </p>
            <h1 id="hot-tubs-h1" className="mt-3 max-w-3xl text-h1-mobile font-semibold text-white lg:text-[48px] lg:leading-[56px]" data-id="element-1466" data-mp="2i6crz6zx0lj3">
              Find the Right Hot Tub for Your Michigan Backyard
            </h1>
            <p className="mt-4 max-w-2xl text-body text-white sm:text-body-lg" data-id="element-1467" data-mp="drkci2ixb3ee">
              Whether you want quiet time after work, room for family, stronger massage in a favorite
              seat or a straightforward first spa, we can help you compare the choices in person
              and plan the practical details with confidence.
            </p>

            <div className="mt-6 flex flex-wrap items-center gap-3" data-id="element-1468" data-mp="8xg7vpx4r7gh">
              <SiteLink to={routes.availableSpas} className="inline-flex min-h-[48px] items-center rounded-btn bg-pegasus-magenta px-6 text-[16px] font-bold text-white transition-colors duration-150 ease-out hover:bg-[#bb2f83]" data-id="element-1469" data-mp="3q6l8hn7mz4ux">
                See Available Spas
              </SiteLink>
              <SiteLink to={routes.location} className="inline-flex min-h-[48px] items-center rounded-btn border-2 border-white bg-white/10 px-6 text-[16px] font-bold text-white transition-colors duration-150 ease-out hover:bg-white hover:text-ink" data-id="element-1470" data-mp="1ffv48yb9dorv">
                Visit Our Brooklyn Showroom
              </SiteLink>
            </div>
            <p className="mt-4 text-[15px] text-white/90" data-id="element-1471" data-mp="2o2mueeqxilfk">
              Sundance Spas, Viking Spas, available models and certified pre-owned options.
            </p>
          </div>
        </div>
      </section>

      {/* Section 1: paths */}
      <section aria-labelledby="paths-heading" className="w-full bg-white" data-id="element-1472" data-mp="2a95m5q3p0v4u">
        <div className="mx-auto max-w-container px-5 py-12 sm:px-8 lg:py-16" data-id="element-1473" data-mp="25a2hw7vjesxx">
          <div className="max-w-3xl" data-id="element-1474" data-mp="25c5i1kqem2gd">
            <p className="text-[14px] font-bold uppercase text-pegasus-blue" data-id="element-1475" data-mp="209xzgcb2jo52">Start Your Search</p>
            <h2 id="paths-heading" className="mt-2 font-display text-h2-mobile font-semibold text-ink lg:text-h2-desktop" data-id="element-1476" data-mp="1w9w74tr8472g">
              Choose the Hot Tub Path That Fits You
            </h2>
            <p className="mt-4 text-body text-ink/80 sm:text-body-lg" data-id="element-1477" data-mp="3qnnchh2is4s3">
              Explore the two brands available through Pegasus, see current opportunities or start
              with a certified pre-owned spa.
            </p>
          </div>

          <ul className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4" data-id="element-1478" data-mp="1m5fue991j62v">
            {paths.map(path => <li key={path.title} data-id="element-1479" data-mp="1q7145lqa348u">
                <SiteLink to={path.href} className="group flex h-full flex-col overflow-hidden rounded-card border border-ink/10 bg-white transition-colors duration-150 ease-out hover:border-pegasus-blue" data-id="element-1480" data-mp="1q5zmuzio5lio">
                  <span className="relative block aspect-[4/3] w-full" data-id="element-1481" data-mp="12gx7jybic4rc">
                    <img src={path.image} alt={path.alt} className="h-full w-full object-cover" loading="lazy" decoding="async" data-id="element-1482" data-mp="17g1iouhzsckb" />
                    {path.modelLabel && <span className="pointer-events-none absolute left-[10px] top-[10px] whitespace-nowrap rounded-[4px] border border-white/15 bg-ink/80 px-2 py-[5px] text-[11px] font-medium leading-none text-white/90 shadow-sm backdrop-blur-[2px] sm:left-3 sm:top-3 sm:text-[12px]" data-id="element-1483" data-mp="2uspucq0gy1mr">
                        {path.modelLabel}
                      </span>}
                  </span>
                  <span className="flex flex-1 flex-col p-5" data-id="element-1484" data-mp="2bu3utfkloiky">
                    <h3 className="text-h3 font-semibold text-ink" data-id="element-1485" data-mp="2bvorbaqh6blg">{path.title}</h3>
                    <span className="mt-2 block text-[16px] leading-6 text-ink/75" data-id="element-1486" data-mp="2gt6xwuh0seqa">
                      {path.copy}
                    </span>
                    <span className="mt-auto pt-4 inline-flex items-center gap-1.5 text-[15.5px] font-bold text-pegasus-blue group-hover:text-pegasus-magenta" data-id="element-1487" data-mp="2pt3lesrob7jr">
                      {path.linkLabel}
                      <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-1488" data-mp="52ungvpl2rbg" />
                    </span>
                  </span>
                </SiteLink>
              </li>)}
          </ul>
        </div>
      </section>

      {/* Section 2: use chooser */}
      <section aria-labelledby="use-heading" className="w-full bg-water" data-id="element-1489" data-mp="1llo9ci5t2qj2">
        <div className="mx-auto max-w-container px-5 py-12 sm:px-8 lg:py-14" data-id="element-1490" data-mp="1grab87zre5ap">
          <div className="max-w-3xl" data-id="element-1491" data-mp="1bsp9u58rtfko">
            <h2 id="use-heading" className="font-display text-h2-mobile font-semibold text-ink lg:text-h2-desktop" data-id="element-1492" data-mp="1brnxzyjxkp1w">
              Start With the Way You Will Use It
            </h2>
            <p className="mt-3 text-body text-ink/80 sm:text-body-lg" data-id="element-1493" data-mp="xtzeau1lnnyf">
              Pick the closest description and bring the questions with you to the showroom.
            </p>
          </div>

          <ul className="mt-8 divide-y divide-ink/15 border-y border-ink/15" data-id="element-1494" data-mp="2s80a161uruua">
            {useChoices.map(choice => {
            const isOpen = openChoice === choice.id;
            return <li key={choice.id} data-id="element-1495" data-mp="36dk64n1al9zu">
                  <button type="button" aria-expanded={isOpen} aria-controls={`use-panel-${choice.id}`} onClick={() => setOpenChoice(isOpen ? '' : choice.id)} className="flex min-h-[56px] w-full items-center justify-between gap-4 py-4 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-pegasus-blue" data-id="element-1496" data-mp="36chegcw5b3iv">
                    <span className="font-display text-[20px] font-semibold leading-8 text-ink sm:text-[23px]" data-id="element-1497" data-mp="3u3muas235vt6">
                      {choice.label}
                    </span>
                    <span className={`shrink-0 text-[15px] font-bold ${isOpen ? 'text-pegasus-magenta' : 'text-pegasus-blue'}`} data-id="element-1498" data-mp="3l2g9w7wbg0un">
                      {isOpen ? 'Hide' : 'Show'}
                    </span>
                  </button>

                  {isOpen && <div id={`use-panel-${choice.id}`} className="pb-6 sm:pl-1" data-id="element-1499" data-mp="222fl3axl4smr">
                      <p className="max-w-3xl text-body leading-7 text-ink/80" data-id="element-1500" data-mp="1x3m7zjm7n3n4">{choice.lead}</p>
                      <p className="mt-4 text-[13.5px] font-bold uppercase tracking-[0.02em] text-pine" data-id="element-1501" data-mp="2b4oxy7gbsy3b">
                        Worth asking
                      </p>
                      <ul className="mt-2 space-y-2" data-id="element-1502" data-mp="1ifrgu7nup7c5">
                        {choice.questions.map(question => <li key={question} className="flex gap-3 text-[16.5px] leading-7 text-ink/85" data-id="element-1503" data-mp="1eepn26cpdnhk">
                            <span className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-pegasus-magenta" aria-hidden="true" data-id="element-1504" data-mp="1nf4bvfovlkpt" />
                            {question}
                          </li>)}
                      </ul>
                    </div>}
                </li>;
          })}
          </ul>

          <SiteLink to={routes.health} className="mt-8 inline-flex min-h-[44px] items-center gap-1.5 text-[16px] font-medium text-pegasus-blue underline decoration-pegasus-blue/40 underline-offset-4 transition-colors duration-150 ease-out hover:text-pegasus-magenta hover:decoration-pegasus-magenta" data-id="element-1505" data-mp="1dcihnar7hfxh">
            Learn About Hot Tub Health Benefits
            <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-1506" data-mp="1w1gsxlenbufh" />
          </SiteLink>
        </div>
      </section>

      {/* Section 3: package ledger */}
      <section aria-labelledby="package-heading" className="w-full bg-white" data-id="element-1507" data-mp="260o17u6iwyvq">
        <div className="mx-auto max-w-container px-5 py-12 sm:px-8 lg:py-16" data-id="element-1508" data-mp="25yldjgsufhu6">
          <div className="grid grid-cols-1 gap-9 lg:grid-cols-12 lg:gap-14" data-id="element-1509" data-mp="6p4vrpkfi8qt">
            <div className="lg:col-span-5" data-id="element-1510" data-mp="bnofrkfgpagj">
              <h2 id="package-heading" className="font-display text-h2-mobile font-semibold text-ink lg:text-h2-desktop" data-id="element-1511" data-mp="bohrz0x6ws62">
                What May Come With a New Spa
              </h2>
              <p className="mt-5 border-l-2 border-pegasus-magenta pl-4 text-body leading-7 text-ink/85 sm:text-body-lg" data-id="element-1512" data-mp="2g437knimy1jx">
                A new-spa package typically may include these items. The exact package varies by spa
                and purchase, so we will confirm what applies before you decide.
              </p>
            </div>

            <ul className="divide-y divide-ink/15 border-y border-ink/15 lg:col-span-7" data-id="element-1513" data-mp="33up0t8nzdh9v">
              {packageItems.map(item => <li key={item} className="py-3.5 font-display text-[19px] font-medium leading-7 text-ink sm:text-[21px]" data-id="element-1514" data-mp="2tvzvefd9tpai">
                  {item}
                </li>)}
            </ul>
          </div>
        </div>
      </section>

      {/* Section 4: journey */}
      <section aria-labelledby="journey-heading" className="w-full bg-water" data-id="element-1515" data-mp="1jxrp0hu3bnqg">
        <div className="mx-auto max-w-container px-5 py-12 sm:px-8 lg:py-16" data-id="element-1516" data-mp="1owulhdw08lmt">
          <h2 id="journey-heading" className="max-w-3xl font-display text-h2-mobile font-semibold text-ink lg:text-h2-desktop" data-id="element-1517" data-mp="1ouqjx2lkvypp">
            From Your Showroom Choice to the First Soak
          </h2>

          <ol className="mt-9 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4 lg:gap-7" data-id="element-1518" data-mp="1170c56aw9f7b">
            {journey.map(step => <li key={step.number} className="border-t-2 border-pegasus-blue/70 pt-4" data-id="element-1519" data-mp="15ztbtfri9xcj">
                <span className="block font-display text-[26px] font-semibold leading-8 text-pegasus-blue" aria-hidden="true" data-id="element-1520" data-mp="s850vfsztex5">
                  {step.number}
                </span>
                <h3 className="mt-1 font-display text-[20px] font-semibold leading-7 text-ink" data-id="element-1521" data-mp="ib0djcnevder">
                  {step.title}
                </h3>
                <p className="mt-2 text-[16px] leading-7 text-ink/75" data-id="element-1522" data-mp="3l5tm8vh6miz">{step.copy}</p>
              </li>)}
          </ol>

          <div className="mt-9 max-w-3xl rounded-card bg-white px-5 py-5 sm:px-6" data-id="element-1523" data-mp="3uu34kbaumevp">
            <div className="flex items-start gap-3.5" data-id="element-1524" data-mp="dkwle2sninfd">
              <InfoIcon className="mt-1 h-5 w-5 shrink-0 text-pegasus-blue" aria-hidden="true" data-id="element-1525" data-mp="8lbkvnxu270j" />
              <p className="text-[16.5px] leading-7 text-ink/85" data-id="element-1526" data-mp="mkjnyrx49za0">
                Electrical work, final hookup and wiring extensions are not performed by Pegasus. Use
                a qualified electrician and follow the exact model requirements.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Section 5: operating cost */}
      <section aria-labelledby="cost-heading" className="w-full bg-white" data-id="element-1527" data-mp="rdmlfb50lsvh">
        <div className="mx-auto max-w-container px-5 py-12 sm:px-8 lg:py-14" data-id="element-1528" data-mp="15c41cmooi12u">
          <div className="max-w-4xl border-t border-ink/15 pt-9" data-id="element-1529" data-mp="11aimwwwhxeqj">
            <h2 id="cost-heading" className="font-display text-[26px] font-semibold leading-9 text-ink sm:text-[30px]" data-id="element-1530" data-mp="27onml3hxytby">
              A Practical Michigan Operating-Cost Estimate
            </h2>
            <p className="mt-5 font-display text-[28px] font-semibold leading-10 text-pegasus-blue sm:text-[36px] sm:leading-[46px]" data-id="element-1531" data-mp="2hnwfdt12m1wq">
              About $1 per day averaged across the year
            </p>
            <p className="mt-4 max-w-2xl text-body leading-8 text-ink/85 sm:text-body-lg" data-id="element-1532" data-mp="1ozh0d6hxvjle">
              For the 220V spas we sell, a rough seasonal example is about $70&ndash;$80 per
              month in winter and $10&ndash;$15 per month in summer.
            </p>
            <p className="mt-3 max-w-2xl text-[15.5px] leading-6 text-ink/65" data-id="element-1533" data-mp="22q3zgzrm3bi0">
              Actual cost varies by model, weather, water-temperature setting, use and local electric
              rates.
            </p>
          </div>
        </div>
      </section>

      {/* Section 6: local guidance */}
      <section aria-labelledby="guidance-heading" className="w-full border-y border-ink/10 bg-white" data-id="element-1534" data-mp="12qa89cu9vnej">
        <div className="mx-auto max-w-container px-5 py-12 sm:px-8 lg:py-16" data-id="element-1535" data-mp="xryzl5fprixw">
          <div className="grid grid-cols-1 items-start gap-10 lg:grid-cols-12 lg:gap-12" data-id="element-1536" data-mp="toas0mr30ncn">
            <div className="lg:col-span-6" data-id="element-1537" data-mp="oqio0ud5xas3">
              <p className="text-[14px] font-bold uppercase text-pine" data-id="element-1538" data-mp="ophg2yrg14el">Why Pegasus</p>
              <h2 id="guidance-heading" className="mt-2 font-display text-h2-mobile font-semibold text-ink lg:text-h2-desktop" data-id="element-1539" data-mp="jro75xyr9wam">
                Local Guidance Before and After the Sale
              </h2>
              <p className="mt-4 text-body leading-8 text-ink/80 sm:text-body-lg" data-id="element-1540" data-mp="5zhoo584rd80">
                We&rsquo;re family-owned and have served Brooklyn and Michigan&rsquo;s Irish Hills since 1977.
                The showroom gives you a place to compare options, ask practical questions and see
                products in person.
              </p>

              <div className="mt-7 divide-y divide-ink/15 border-y border-ink/15" data-id="element-1541" data-mp="3qx1vo84l712l">
                <div className="py-5" data-id="element-1542" data-mp="3m0bd9e5b7te0">
                  <h3 className="font-display text-[20px] font-semibold leading-7 text-ink" data-id="element-1543" data-mp="3lx71nvpilvdb">
                    Before delivery
                  </h3>
                  <p className="mt-2 text-[16.5px] leading-7 text-ink/75" data-id="element-1544" data-mp="3c1vkz7ujqe2x">
                    Compare seating and layout in person, review the site and the access route, and
                    confirm the package and the exact model requirements before you decide.
                  </p>
                </div>
                <div className="py-5" data-id="element-1545" data-mp="2yaqsqqtico2m">
                  <h3 className="font-display text-[20px] font-semibold leading-7 text-ink" data-id="element-1546" data-mp="2y7l0x3thv837">
                    After delivery
                  </h3>
                  <p className="mt-2 text-[16.5px] leading-7 text-ink/75" data-id="element-1547" data-mp="2oagf553k9emx">
                    Call us first with warranty or ownership questions. We can check the applicable
                    coverage, may troubleshoot by phone and can schedule through our own service
                    department. Manufacturer coverage varies by model and circumstance.
                  </p>
                </div>
              </div>

              <div className="mt-7 flex flex-wrap items-center gap-4" data-id="element-1548" data-mp="18t3btifls6jk">
                <SiteLink to={routes.about} className="inline-flex min-h-[48px] items-center rounded-btn bg-pegasus-magenta px-6 text-[16px] font-bold text-white transition-colors duration-150 ease-out hover:bg-[#bb2f83]" data-id="element-1549" data-mp="1drwtln1xajpj">
                  Why Choose Pegasus
                </SiteLink>
                <SiteLink to={routes.service} className="inline-flex min-h-[44px] items-center gap-1.5 text-[16px] font-medium text-pegasus-blue underline decoration-pegasus-blue/40 underline-offset-4 transition-colors duration-150 ease-out hover:text-pegasus-magenta hover:decoration-pegasus-magenta" data-id="element-1550" data-mp="q0ra8xwrx9ah">
                  Spa Service &amp; Repair
                  <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-1551" data-mp="3qfd3pnpmcger" />
                </SiteLink>
              </div>
            </div>

            <div className="lg:col-span-6" data-id="element-1552" data-mp="41bujd5aapsc">
              <div className="aspect-[4/3] w-full overflow-hidden rounded-card" data-id="element-1553" data-mp="413ljwca84mf">
                <img src={images.deliveryDock} alt={deliveryDockAlt} className="h-full w-full object-cover object-center" loading="lazy" decoding="async" data-id="element-1554" data-mp="3v8xwn1ccuk73" />
              </div>
              <p className="mt-3 text-[15px] leading-6 text-ink/65" data-id="element-1555" data-mp="3hh19lbpjn0hk">
                One of our own lake-access deliveries. We deliver with our own team, never a
                contract crew.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Section 7: next step */}
      <section aria-labelledby="next-step-heading" className="w-full bg-white" data-id="element-1556" data-mp="rrz6c9iblzk6">
        <div className="mx-auto max-w-container px-5 py-12 sm:px-8 lg:py-14" data-id="element-1557" data-mp="29p2npf9sevdg">
          <h2 id="next-step-heading" className="font-display text-h2-mobile font-semibold text-ink lg:text-h2-desktop" data-id="element-1558" data-mp="29r5hmae298ng">
            Plan the Next Step
          </h2>

          <div className="mt-7 grid grid-cols-1 gap-8 border-t border-ink/10 pt-7 lg:grid-cols-2 lg:gap-12" data-id="element-1559" data-mp="25n7m09ny2fz5">
            <div data-id="element-1560" data-mp="2td1xamsm7wha">
              <h3 className="text-[20px] font-semibold leading-7 text-ink" data-id="element-1561" data-mp="2tcjqiw8btzv4">Payment Options</h3>
              <p className="mt-2 text-[16px] leading-6 text-ink/75" data-id="element-1562" data-mp="2xewzdtidedw4">
                Ask us which financing or payment information is currently available.
              </p>
              <SiteLink to={routes.financing} className="mt-3 inline-flex min-h-[44px] items-center gap-1.5 text-[16px] font-bold text-pegasus-blue underline decoration-pegasus-blue/40 underline-offset-4 transition-colors duration-150 ease-out hover:text-pegasus-magenta hover:decoration-pegasus-magenta" data-id="element-1563" data-mp="2om3f1ho2gobe">
                Payment &amp; Financing
                <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-1564" data-mp="1se7470xe8jtk" />
              </SiteLink>
            </div>

            <div className="border-t border-ink/10 pt-8 lg:border-l lg:border-t-0 lg:pl-12 lg:pt-0" data-id="element-1565" data-mp="1jflkv3k3l0rq">
              <h3 className="text-[20px] font-semibold leading-7 text-ink" data-id="element-1566" data-mp="1eh1zb49f1qzw">Visit the Showroom</h3>
              <p className="mt-2 text-[16px] leading-6 text-ink/75" data-id="element-1567" data-mp="2b6t4bxfc2wjy">
                Talk through brands, placement, ownership and the experience you want from your hot
                tub.
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-2" data-id="element-1568" data-mp="22e7mcjjj0tw6">
                <SiteLink to={routes.location} className="inline-flex min-h-[44px] items-center gap-1.5 text-[16px] font-bold text-pegasus-blue underline decoration-pegasus-blue/40 underline-offset-4 transition-colors duration-150 ease-out hover:text-pegasus-magenta hover:decoration-pegasus-magenta" data-id="element-1569" data-mp="1xfo3wt45608k">
                  <ClockIcon className="h-4 w-4" aria-hidden="true" data-id="element-1570" data-mp="2xh19u27hnrbp" />
                  Location &amp; Hours
                </SiteLink>
                <a href={business.phoneHref} className="inline-flex min-h-[44px] items-center gap-2 text-[16px] font-medium text-ink transition-colors duration-150 ease-out hover:text-pegasus-blue" data-id="element-1571" data-mp="37dnryj8fk4a1">
                  <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-1572" data-mp="3gdj9d5skwvii" />
                  Call or text {business.phone}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related links */}
      <section aria-label="Related pages" className="w-full border-t border-ink/10 bg-water" data-id="element-1573" data-mp="3v38ns2pomw0q">
        <div className="mx-auto max-w-container px-5 py-6 sm:px-8" data-id="element-1574" data-mp="3v51jwo2o5xuh">
          <ul className="flex flex-col gap-x-8 gap-y-1 sm:flex-row sm:flex-wrap sm:items-center" data-id="element-1575" data-mp="310rg3r0jycn">
            {relatedLinks.map(link => <li key={link.label} data-id="element-1576" data-mp="241e11jz9uinx">
                <SiteLink to={link.href} className="inline-flex min-h-[44px] items-center gap-1.5 text-[16px] font-medium text-pegasus-blue underline decoration-pegasus-blue/40 underline-offset-4 transition-colors duration-150 ease-out hover:text-pegasus-magenta hover:decoration-pegasus-magenta" data-id="element-1577" data-mp="242p0j7lop498">
                  {link.label}
                  <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-1578" data-mp="2nov8je8rabbx" />
                </SiteLink>
              </li>)}
          </ul>
        </div>
      </section>
    </main>;
}